import React from 'react'
import { useContactRequestContext } from './ContactRequestContext'
import { StepContainer } from './components/StepContainer'
import { LoadingStep } from './components/LoadingStep'
import FbButton from 'components/FbUI/FbButton'
import { useHasUnlimitedCredits } from '../../../hooks/useHasUnlimitedCredits'
import { useFeatureFlagService } from '../../../utils/useFeatureFlagService'
import { useCreditBalance } from '../../../hooks/useCreditBalance'

export const SummaryStep: React.FC = () => {
  const {
    createJob,
    loading,
    error,
    handleClose,
    prevStep,
    maxContactsPerCompany,
    selectedCompanyIds,
    isMultiType,
    subcategories,
  } = useContactRequestContext()

  const hasUnlimitedCredits = useHasUnlimitedCredits()
  const { enableContactCredits } = useFeatureFlagService()
  const { credits, isLoading: creditsLoading } = useCreditBalance()

  // Calculate required credits and check if user has enough
  const requiredCredits = selectedCompanyIds.length * maxContactsPerCompany
  const hasEnoughCredits =
    hasUnlimitedCredits || credits.available >= requiredCredits
  const eligibleTypeCount = isMultiType
    ? subcategories.filter((s) => s.contact_request_allowed).length
    : 0

  const handleSubmit = () => {
    // Only check credits when user doesn't have unlimited credits
    if (!hasUnlimitedCredits && !hasEnoughCredits) {
      alert(
        `Not enough credits available. Need ${requiredCredits} credits but only have ${credits.available}.`
      )
      return
    }

    // Call the createJob function with the stored values
    createJob(maxContactsPerCompany, selectedCompanyIds)
  }

  // Show loading spinner if we're still loading data
  if (creditsLoading) {
    return <LoadingStep title="Review and Confirm" currentStep={4} />
  }

  return (
    <StepContainer
      title="Review and Confirm"
      subtitle={`You're about to request ${maxContactsPerCompany} contact${maxContactsPerCompany > 1 ? 's' : ''} per company for ${selectedCompanyIds.length} companies.`}
      error={error}
      handleClose={handleClose}
      onBack={prevStep}
      onContinue={handleSubmit}
      continueButtonText={loading ? 'Requesting...' : 'Request Contacts'}
      canContinue={
        !loading &&
        (hasEnoughCredits || hasUnlimitedCredits) &&
        selectedCompanyIds.length > 0
      }
      loading={loading}
      currentStep={4}
      totalSteps={4}
    >
      {selectedCompanyIds.length > 0 ? (
        <>
          {/* Contact request summary */}
          <div className="mb-4 p-3 ounded border border-gray-200">
            <h3 className="text-sm font-medium mb-2">
              Contact Request Summary
            </h3>
            <div className="flex justify-between text-sm">
              <span>Total companies:</span>
              <span className="font-medium">{selectedCompanyIds.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Contacts per company:</span>
              <span className="font-medium">{maxContactsPerCompany}</span>
            </div>
            <div className="flex justify-between text-sm font-medium border-t border-gray-200 mt-1 pt-1">
              <span>Total contacts to request:</span>
              <span
                className={
                  !hasUnlimitedCredits && !hasEnoughCredits
                    ? 'text-red-600'
                    : ''
                }
              >
                {requiredCredits}
              </span>
            </div>
            {eligibleTypeCount > 1 && (
              <p className="mt-2 text-xs text-fg-secondary">
                This will create {eligibleTypeCount} contact requests (one per
                company type).
              </p>
            )}
          </div>

          {/* Credit information section */}
          {enableContactCredits && (
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
              <h3 className="text-sm font-medium mb-2">Contact Credits</h3>
              {creditsLoading ? (
                <p className="text-sm text-gray-500">
                  Loading credit information...
                </p>
              ) : (
                <>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Available Credits:</span>
                    <span className="font-medium">
                      {hasUnlimitedCredits ? '∞' : credits.available}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Used:</span>
                    <span>{credits.used}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Pending:</span>
                    <span>{hasUnlimitedCredits ? '-' : credits.pending}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-3">
                    <span>Total Credits:</span>
                    <span>{hasUnlimitedCredits ? '∞' : credits.total}</span>
                  </div>

                  <div className="flex justify-between text-sm border-t border-gray-200 pt-2 mb-1">
                    <span>Required for this request:</span>
                    <span className="font-medium">{requiredCredits}</span>
                  </div>

                  {!hasUnlimitedCredits && !hasEnoughCredits && (
                    <div className="mt-2">
                      <p className="text-red-600 text-sm mb-2">
                        You don&apos;t have enough credits for this request. You
                        need {requiredCredits - credits.available} more credits.
                      </p>
                      <FbButton
                        size="sm"
                        onClick={() =>
                          (window.location.href = '/contacts/requests')
                        }
                        className="w-full"
                      >
                        Purchase Credits
                      </FbButton>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-6">
          <p className="text-gray-500">
            No companies selected. Please go back and select companies.
          </p>
        </div>
      )}
    </StepContainer>
  )
}
